<script setup lang="ts">
import {ref} from "vue";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import {Button} from "@/core/components/ui/button";
import Icon from "@/core/components/icon/Icon.vue";
import {CloudUploadIcon, Eye, Loader2, Mail, Trash2} from "@lucide/vue";
import {secureMessagingApi, surveyAnswerInstanceApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import {apiErrorMessage, apiErrorStatus, isAuthorizationError} from "@/core/http/apiErrorMessage";
import formatHelper from "@/core/helpers/formatHelper";
import {SurveyAnswerInstanceIndex} from "@/core/interfaces/survey/Survey";
import {openDMP} from "@/core/helpers/dmp";
import MSSanteDialog from "@/mssante/components/MSSanteDialog.vue";
import useSurveyMSSantePrefills from "@/mssante/composables/useSurveyMSSantePrefills";
import {MSSantePrefill, SurveySecureMessagingDocument} from "@/mssante/interfaces/SecureMessaging";

const props = defineProps<{
    instance: SurveyAnswerInstanceIndex;
}>();

const emit = defineEmits<{
    (event: 'open'): void;
    (event: 'deleted'): void;
}>();

const {showSuccess, showError} = useToastStore();

/**
 * Whether the deletion confirmation is up. Held here rather than opened by a trigger
 * of its own: the menu closes on the click and would take a nested trigger down with it.
 */
const confirming = ref(false);
const deleting = ref(false);

const {patient, toPrefills} = useSurveyMSSantePrefills(() => props.instance.consumer_key);

const preparing = ref(false);
const composing = ref(false);
const mssantePrefills = ref<MSSantePrefill[]>([]);

/**
 * The survey report the HDS exports for the instance, the result the message is about.
 */
const report = (): SurveySecureMessagingDocument => ({
    name: `Compte rendu — ${props.instance.survey.name}.pdf`,
    url: `survey-answer-instances/${props.instance.id}/export`,
    source: 'hds',
});

/**
 * Open the MSSanté dialog on the messages the API renders for the survey, as at the
 * end of a survey, each one carrying the survey report: the generic message has no
 * attachment, and a template may not declare it.
 */
const composeMSSante = async () => {
    preparing.value = true;

    try {
        const rendered = await secureMessagingApi().surveyPrefill(props.instance.id);
        const document = report();

        mssantePrefills.value = toPrefills(rendered).map(prefill => ({
            ...prefill,
            documents: prefill.documents?.some(declared => declared.url === document.url)
                ? prefill.documents
                : [...(prefill.documents ?? []), document],
        }));
        composing.value = true;
    } catch {
        showError({content: 'Le message MSSanté n\'a pas pu être préparé.'});
    } finally {
        preparing.value = false;
    }
};

/**
 * Delete the instance from the patient history. The confirmation stays up while the
 * call runs, and after a failure worth retrying.
 *
 * A refusal closes it: retrying would fail the same way. The server refuses with a 409
 * an instance another record points to (a pathway step, an interview, a task), and the
 * 401/403 already got their toast from the response interceptor.
 */
const remove = async () => {
    deleting.value = true;

    try {
        await surveyAnswerInstanceApi().destroy(props.instance.id);
        confirming.value = false;
        showSuccess({content: 'Le résultat du questionnaire a été supprimé.'});
        emit('deleted');
    } catch (error) {
        if (apiErrorStatus(error) === 409) {
            confirming.value = false;
            showError({content: 'Ce questionnaire ne peut pas être supprimé : il est rattaché à un parcours, à un entretien ou à une tâche.'});
        } else if (isAuthorizationError(error)) {
            confirming.value = false;
        } else {
            showError({content: apiErrorMessage(error, 'Le résultat du questionnaire n\'a pas pu être supprimé.')});
        }
    } finally {
        deleting.value = false;
    }
};
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child :disabled="preparing">
      <Loader2 v-if="preparing" class="size-[30px] p-1 animate-spin text-gray-500" />
      <Icon v-else name="mdi-dots-vertical" :size="30" color="gray" />
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-56" side="top">
      <DropdownMenuGroup>
        <DropdownMenuItem @click="openDMP">
          <CloudUploadIcon />
          Accès <img src="@/assets/images/dmp.jpg" class="h-8" alt="DMP">
        </DropdownMenuItem>
        <DropdownMenuItem @click="composeMSSante">
          <Mail />
          Envoyer <img class="w-16" src="@/assets/images/logos/logo-ms-sante.png" alt="MSSanté">
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem @click="emit('open')">
        <Eye />
        Voir le résultat
      </DropdownMenuItem>
      <template v-if="instance.deletable">
        <DropdownMenuSeparator />
        <DropdownMenuItem
          class="cursor-pointer text-red-700 focus:text-red-700"
          @click="confirming = true"
        >
          <Trash2 />
          Supprimer
        </DropdownMenuItem>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>

  <MSSanteDialog
    v-model:open="composing"
    :prefills="mssantePrefills"
    :consumer-key="instance.consumer_key"
    :patient-name="patient?.information?.name"
    :customer-id="patient?.id"
    :source="{type: 'survey-answer', id: instance.id}"
  />

  <AlertDialog v-model:open="confirming">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Supprimer ce résultat de questionnaire ?</AlertDialogTitle>
        <AlertDialogDescription>
          Le résultat « {{ instance.survey.name }} » du {{ formatHelper.readableDate(instance.created_at) }}
          sera retiré de la fiche patient, avec les réponses saisies. Cette action est définitive.
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter class="items-center">
        <AlertDialogCancel :disabled="deleting">
          Annuler
        </AlertDialogCancel>

        <Button
          variant="destructive"
          class="bg-red-600 hover:bg-red-600/90"
          :disabled="deleting"
          @click="remove"
        >
          Supprimer
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
